import React from 'react';
import {Label, Tooltip, type IconName} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {
  CONTENT_SUBMISSION_STATUS,
  ContentSubmissionState,
} from '@/types/contentSubmission';
import Tag from '@/components/tag';
import type {TagColor, TagVariant} from '@/components/tag';

export type ContentSubmissionTagVariant =
  | 'approved'
  | 'editsRequested'
  | 'awaiting'
  | 'awaitingFinalApproval'
  | 'new'
  | 'editsMade'
  | 'archived'
  | 'overdue'
  | 'aiScore'
  | 'postProduction';

export type ContentSubmissionTagProps = {
  testID?: string;
  variant: ContentSubmissionTagVariant;
  departments?: string[];
  score?: number | null;
  count?: number;
};

type TagConfig = {
  label?: string;
  icon?: IconName;
  color: TagColor;
  variant: TagVariant;
};

function getTagConfig(
  variant: ContentSubmissionTagVariant,
  departments?: string[],
  score?: number | null,
  count?: number
): TagConfig {
  switch (variant) {
    case 'postProduction':
      return {
        label:
          count && count > 1
            ? translate('Post-prod · {count}', {count})
            : translate('Post-prod'),
        icon: 'Features-Resize',
        color: 'neutral',
        variant: 'text',
      };
    case 'approved':
      return {
        label: translate('Approved'),
        color: 'success',
        variant: 'filled',
      };
    case 'new':
      return {
        label: translate('New'),
        color: 'complementary',
        variant: 'filled',
      };
    case 'editsMade':
      return {
        label: translate('Edits made'),
        color: 'complementary',
        variant: 'filled',
      };
    case 'awaiting':
      return {
        label: translate('Awaiting: {departments}', {
          departments: (departments ?? []).join(', '),
        }),
        color: 'info',
        variant: 'filled',
      };
    case 'awaitingFinalApproval':
      return {
        label: translate('Awaiting final approval'),
        color: 'info',
        variant: 'filled',
      };
    case 'editsRequested':
      return {
        label: translate('Edits requested'),
        color: 'warning',
        variant: 'filled',
      };
    case 'archived':
      return {
        label: translate('Archived'),
        color: 'neutral',
        variant: 'filled',
      };
    case 'overdue':
      return {
        icon: 'Actions-Alert-Line',
        color: 'danger',
        variant: 'outlined',
      };
    case 'aiScore':
      return {
        label: `${score}/10`,
        icon: 'Features-Effects',
        color: 'neutral',
        variant: 'text',
      };
    default:
      return {
        label: translate('New'),
        color: 'complementary',
        variant: 'filled',
      };
  }
}

export function mapStateToTagVariant(
  state: ContentSubmissionState,
  pendingDepartments?: string[],
  approvedCount = 0,
  versionCount?: number
): ContentSubmissionTagVariant {
  // Revisions (version > 1) are shown as "Edits made" rather than "New".
  const newOrEditsMade: ContentSubmissionTagVariant =
    versionCount && versionCount > 1 ? 'editsMade' : 'new';

  switch (state) {
    case CONTENT_SUBMISSION_STATUS.PENDING:
      if (pendingDepartments?.length && approvedCount > 0) {
        return 'awaiting';
      }
      if (!pendingDepartments?.length && approvedCount > 0) {
        return 'awaitingFinalApproval';
      }
      return newOrEditsMade;
    case CONTENT_SUBMISSION_STATUS.APPROVED:
      return 'approved';
    case CONTENT_SUBMISSION_STATUS.REJECTED:
      return 'editsRequested';
    case CONTENT_SUBMISSION_STATUS.ARCHIVED:
      return 'archived';
    default:
      return newOrEditsMade;
  }
}

function ContentSubmissionTag({
  testID = 'content-submission-tag',
  variant,
  departments,
  score,
  count,
}: ContentSubmissionTagProps) {
  const config = getTagConfig(variant, departments, score, count);

  const tag = (
    <Tag
      testID={testID}
      label={
        config.label ? (
          <Label size="sm" color="currentColor" fontWeight={500}>
            {config.label}
          </Label>
        ) : undefined
      }
      icon={config.icon}
      color={config.color}
      variant={config.variant}
    />
  );

  if (variant === 'aiScore') {
    return (
      <Tooltip
        type="dark"
        title={translate('AI Analysis Score')}
        placement="top"
        arrow
      >
        <span>{tag}</span>
      </Tooltip>
    );
  }

  return tag;
}

export default ContentSubmissionTag;
